import { useRouter } from 'vue-router'
import { useModalStore } from '@/stores/modal.store'
import { useConversationStore } from '@/stores/conversation.store'
import { useAuthStore } from '@/stores/auth.store'
import { usePaymentStore } from '@/stores/payment.store'
import {
    storeIntent,
    storeTargetCreator,
    getIntent,
    getTargetCreator,
    clearAllIntentData,
} from '@/utils/clientIntent'
import {
    ensureAnonymous,
    ensureProfileSetup,
    ensureRegistered,
    createAnonymousUser,
} from '@/guards'

export function useGuardedActions() {
    const router = useRouter()
    const modalStore = useModalStore()
    const conversationStore = useConversationStore()
    const authStore = useAuthStore()
    const paymentStore = usePaymentStore()

    const executeHandler = async (handler) => {
        if (handler === 'redirect-profile-setup') {
            await router.push({ name: 'client-profile-setup' })
            return { handled: true }
        }
        if (handler === 'show-auth-modal') {
            modalStore.openModal({ darkMode: true, keepOpenOnAuth: true })
            return { handled: true }
        }
        return { handled: false }
    }

    const runGuards = async (...guards) => {
        for (const guard of guards) {
            let result = guard(authStore)
            if (result.pass) {
                continue
            }

            if (result.handler === 'create-anon') {
                const created = await createAnonymousUser(authStore)
                if (created?.success) {
                    // Re-check the same guard now that we have an anon user
                    result = guard(authStore)
                    if (result.pass) {
                        continue
                    }
                } else {
                    await executeHandler('show-auth-modal')
                    return { passed: false, handler: 'show-auth-modal' }
                }
            }

            if (!result.pass) {
                await executeHandler(result.handler)
                return { passed: false, handler: result.handler }
            }
        }
        return { passed: true }
    }

    const startChat = async (creator, options = {}) => {
        if (!creator?._id) return false
        const { skipStoreIntent = false, query = {} } = options

        if (!skipStoreIntent) {
            storeIntent({ action: 'chat' })
            storeTargetCreator(creator)
        }

        const guardResult = await runGuards(ensureAnonymous, ensureProfileSetup)

        if (!guardResult.passed) {
            return false
        }

        const hasExisting = await conversationStore.hasConversationWithUser(
            creator._id
        )

        if (hasExisting) {
            const success = await conversationStore.findOrCreateConversationWithUser(
                creator._id,
                creator.username,
                { skipSetupGate: true }
            )

            if (success) {
                clearAllIntentData()
                await router.push({ name: 'client-chats', query })
                return true
            }

            return false
        }

        await router.push({ name: 'client-pre-chat', query })
        return true
    }

    const startBooking = async (creator, options = {}) => {
        if (!creator?._id) return false
        const { skipStoreIntent = false, query = {} } = options

        if (!skipStoreIntent) {
            storeIntent({ action: 'book_session' })
            storeTargetCreator(creator)
        }

        const guardResult = await runGuards(ensureAnonymous)
        if (!guardResult.passed) {
            return false
        }

        await router.push({ name: 'client-booking', query })
        return true
    }

    const addFunds = async () => {
        const guardResult = await runGuards(ensureRegistered)
        return guardResult.passed
    }

    const purchaseContent = async (item, creator) => {
        storeIntent({
            action: 'buy_content',
            data: {
                itemId: item.id || item._id,
                creatorUsername: creator.username,
            },
        })
        storeTargetCreator(creator)
        const guardResult = await runGuards(ensureRegistered)
        return guardResult.passed
    }

    const initiateCall = async (callType, creator) => {
        storeIntent({
            action: 'start_call',
            data: {
                callType,
                creatorId: creator._id || creator.id,
                creatorUsername: creator.username,
            },
        })
        storeTargetCreator(creator)
        const guardResult = await runGuards(ensureRegistered)
        return guardResult.passed
    }

    const resumeAction = async () => {
        const pendingAction = localStorage.getItem('pendingAction')
        if (pendingAction === 'addFunds') {
            localStorage.removeItem('pendingAction')
            paymentStore.state.showBuyStarsModal = true
            return true
        }

        const intent = getIntent()
        const targetCreator = getTargetCreator()
        if (!intent?.action) return false

        if (intent.action === 'chat' && targetCreator?._id) {
            return await startChat(targetCreator, { skipStoreIntent: true })
        }

        if (intent.action === 'book_session' && targetCreator?._id) {
            return await startBooking(targetCreator, { skipStoreIntent: true })
        }

        if (
            intent.action === 'buy-listing-item' ||
            intent.action === 'buy_content'
        ) {
            const username =
                targetCreator?.username || intent?.data?.creatorUsername
            const itemId = intent?.data?.itemId || intent?.itemId
            if (username) {
                await router.push({
                    name: 'creator-profile',
                    params: { slug: username },
                    query: itemId ? { openItem: itemId } : {},
                })
                clearAllIntentData()
                return true
            }
        }

        if (intent.action === 'start_call') {
            const callType = intent?.data?.callType
            if (targetCreator?._id && callType) {
                return await startChat(targetCreator, {
                    skipStoreIntent: true,
                    query: { openCall: callType },
                })
            }
        }

        return false
    }

    return {
        runGuards,
        startChat,
        startBooking,
        addFunds,
        purchaseContent,
        initiateCall,
        resumeAction,
    }
}
